import { useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchOrderDetail } from "../api/orders";
import type { OrderDetailLine, OrderRow } from "../api/orders";
import { fetchProject } from "../api/projects";
import type { ProjectSummary } from "../api/projects";
import { DetailBackNav, DetailQueryStatus } from "../components/DetailPageNav";
import DocumentPdfActions from "../components/DocumentPdfActions";
import KpiCard from "../components/KpiCard";
import {
  RawRecordModal,
  type RawRecordModalData,
} from "../components/RawRecordModal";
import {
  SourceBadge as SourceKindBadge,
  sourceBadgeKindFromSourceKind,
} from "../components/SourceBadge";
import {
  backTargetFromLocationState,
  type DetailLocationState,
} from "../lib/backNavigation";
import { isSpecialArticleKey } from "../lib/specialArticle";
import { cleanDescription } from "../lib/description";
import {
  costCenterListHref,
  deliveryAddressCostCenterHref,
  projectCostCenterHref,
} from "../lib/costCenterDisplay";
import {
  formatDate,
  formatEuro,
  formatPercent,
  formatQty,
  toNumber,
} from "../lib/format";
import { priceVarianceHref } from "../lib/priceVarianceLinks";
import { projectDisplay } from "../lib/projectDisplay";

type SourceFilter = "all" | "pdf" | "ugl";

export default function OrderDetailPage() {
  const { orderKey } = useParams<{ orderKey: string }>();
  const key = orderKey ?? "";
  const location = useLocation();
  const fromState = (location.state as DetailLocationState)?.from;
  const back = backTargetFromLocationState({
    from: fromState,
    fallback: { to: "/orders", label: "← zurück zu den Aufträgen" },
  });
  const [rawRecord, setRawRecord] = useState<RawRecordModalData | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");

  const detailQuery = useQuery({
    queryKey: ["orders", "detail", key],
    queryFn: () => fetchOrderDetail(key),
    enabled: key.length > 0,
  });

  const detail = detailQuery.data;
  const order = detail?.order ?? null;
  const lines = detail?.lines ?? [];

  const projectKey = order?.project_key ?? null;
  const projectQuery = useQuery({
    queryKey: ["project", projectKey],
    queryFn: () => fetchProject(projectKey!),
    enabled: Boolean(projectKey),
    staleTime: 300_000,
  });
  const project = projectQuery.data ?? null;
  const sourceCounts = useMemo(() => countSources(lines), [lines]);
  const sourceFilterCounts = useMemo(
    () => ({
      all: lines.length,
      pdf: sourceCounts.pdf_rechnung + sourceCounts.pdf_auftrag,
      ugl: sourceCounts.ugl_rechnung + sourceCounts.ugl_vorgang,
    }),
    [lines.length, sourceCounts],
  );
  const visibleLines = useMemo(
    () => filterSourceLines(lines, sourceFilter),
    [lines, sourceFilter],
  );
  const title = order?.order_number ?? order?.primary_doc_number ?? key;
  const buyer = order?.buyer_name ?? order?.buyer_email;

  return (
    <main className="mx-auto max-w-7xl px-6 py-8">
      <DetailBackNav
        back={back}
        home={{ to: "/orders", label: "Auftragsübersicht" }}
      />

      <DetailQueryStatus
        isLoading={detailQuery.isLoading}
        error={detailQuery.isError ? detailQuery.error : null}
        loadingLabel="Lade Auftrag …"
        notFoundLabel="Auftrag nicht gefunden."
      />

      {order ? (
        <>
          <header className="mt-4">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900">
                Auftrag {title}
              </h1>
              <StatusBadge value={order.reconciliation_state} />
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {[order.supplier, formatDate(order.order_date), buyer]
                .filter((part) => part && part !== "–")
                .join(" · ")}
            </p>
          </header>

          <section className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiCard
              label="Ausgaben"
              value={formatEuro(order.total_expenses)}
              hint={`${formatQty(order.line_count)} Positionen`}
            />
            <KpiCard
              label="Artikel"
              value={formatQty(order.article_count)}
              hint={`${formatQty(lines.length)} Positionen angezeigt`}
            />
            <KpiCard
              label="Quellen"
              value={sourceSummary(sourceCounts)}
              hint={order.source_summary ?? "–"}
            />
            <KpiCard
              label="Kostenstelle"
              value={order.has_cost_center_context ? "vorhanden" : "fehlt"}
              hint={costCenterHint(order)}
            />
          </section>

          <SourceEvidencePanel order={order} sourceCounts={sourceCounts} />
          <SourceGapReview order={order} sourceCounts={sourceCounts} />

          <ContextPanel
            order={order}
            project={project}
            fromPath={location.pathname}
          />

          <section className="mt-8">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-baseline gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                Positionen
                <span className="text-xs font-normal normal-case text-slate-400">
                  PDF- und UGL-Nachweise für diesen Auftrag
                </span>
              </h2>
              <SourceFilterTabs
                value={sourceFilter}
                counts={sourceFilterCounts}
                onChange={setSourceFilter}
              />
            </div>
            <OrderLinesTable
              rows={visibleLines}
              emptyMessage={sourceEmptyMessage(sourceFilter)}
              fromPath={location.pathname}
              onOpenRawLine={(line) => setRawRecord(rawRecordFromLine(line))}
            />
          </section>

          <RawRecordModal
            record={rawRecord}
            onClose={() => setRawRecord(null)}
          />
        </>
      ) : null}
    </main>
  );
}

function filterSourceLines(
  rows: OrderDetailLine[],
  sourceFilter: SourceFilter,
): OrderDetailLine[] {
  if (sourceFilter === "pdf") {
    return rows.filter((row) => row.source_kind.startsWith("pdf"));
  }
  if (sourceFilter === "ugl") {
    return rows.filter((row) => row.source_kind.startsWith("ugl"));
  }
  return rows;
}

function sourceEmptyMessage(sourceFilter: SourceFilter): string {
  if (sourceFilter === "pdf") return "Keine PDF-Positionen gefunden.";
  if (sourceFilter === "ugl") return "Keine UGL-Positionen gefunden.";
  return "Keine Positionen gefunden.";
}

function SourceFilterTabs({
  value,
  counts,
  onChange,
}: {
  value: SourceFilter;
  counts: Record<SourceFilter, number>;
  onChange: (next: SourceFilter) => void;
}) {
  const options: Array<{ value: SourceFilter; label: string }> = [
    { value: "all", label: "Alle" },
    { value: "pdf", label: "PDF" },
    { value: "ugl", label: "UGL" },
  ];
  return (
    <div
      className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-0.5 text-xs shadow-sm"
      aria-label="Positionen nach Quelle filtern"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={
            value === option.value
              ? "rounded bg-emerald-50 px-2.5 py-1 font-medium text-emerald-800"
              : "rounded px-2.5 py-1 text-slate-500 hover:text-slate-900"
          }
        >
          {option.label}
          {" "}
          <span className="ml-1 tabular-nums">{counts[option.value]}</span>
        </button>
      ))}
    </div>
  );
}

function SourceEvidencePanel({
  order,
  sourceCounts,
}: {
  order: OrderRow;
  sourceCounts: Record<string, number>;
}) {
  const uglCount = sourceCounts.ugl_vorgang + sourceCounts.ugl_rechnung;
  return (
    <section className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
      <SourceCard title="PDF Rechnung" active={order.has_pdf_invoice_evidence}>
        {order.pdf_invoice_doc_id ? (
          <DocumentPdfActions
            docId={order.pdf_invoice_doc_id}
            docNumber={order.pdf_invoice_doc_number}
            linePos={null}
          />
        ) : (
          "kein Nachweis"
        )}
      </SourceCard>
      <SourceCard title="PDF Auftrag" active={order.has_pdf_order_evidence}>
        {order.pdf_order_doc_id ? (
          <DocumentPdfActions
            docId={order.pdf_order_doc_id}
            docNumber={order.pdf_order_doc_number}
            linePos={null}
          />
        ) : (
          "kein Nachweis"
        )}
      </SourceCard>
      <SourceCard title="UGL" active={order.has_ugl_evidence}>
        {uglCount > 0
          ? `${uglCount.toLocaleString("de-AT")} Positionen`
          : "kein Nachweis"}
      </SourceCard>
    </section>
  );
}

function SourceCard({
  title,
  active,
  children,
}: {
  title: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </div>
        <span
          className={
            active
              ? "rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"
              : "rounded bg-slate-50 px-2 py-0.5 text-xs text-slate-400"
          }
        >
          {active ? "vorhanden" : "fehlt"}
        </span>
      </div>
      <div className="mt-2 text-slate-700">{children}</div>
    </div>
  );
}

function SourceGapReview({
  order,
  sourceCounts,
}: {
  order: OrderRow;
  sourceCounts: Record<string, number>;
}) {
  const items = sourceGapItems(order, sourceCounts);
  return (
    <section className="mt-6 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Abgleich
        </h2>
        <StatusBadge value={order.reconciliation_state} />
      </div>
      <ul className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
        {items.map((item) => (
          <li
            key={item}
            className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-slate-700"
          >
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
}

function sourceGapItems(
  order: OrderRow,
  sourceCounts: Record<string, number>,
): string[] {
  const items: string[] = [];
  const hasPdf = order.has_pdf_invoice_evidence || order.has_pdf_order_evidence;
  const hasUgl =
    order.has_ugl_evidence ||
    sourceCounts.ugl_rechnung + sourceCounts.ugl_vorgang > 0;

  if (hasUgl && !hasPdf) {
    items.push("Nur UGL: kein PDF-Auftrag oder PDF-Rechnung verknüpft.");
  } else if (hasPdf && !hasUgl) {
    items.push("Nur PDF: kein UGL-Nachweis verknüpft.");
  }

  if (order.has_pdf_invoice_evidence && !order.has_pdf_order_evidence) {
    items.push("PDF-Rechnung ohne verknüpften PDF-Auftrag.");
  }
  if (order.has_pdf_order_evidence && !order.has_pdf_invoice_evidence) {
    items.push("PDF-Auftrag ohne verknüpfte PDF-Rechnung.");
  }
  if (order.line_reconciliation_state === "line_mismatch") {
    items.push(
      "Positionen prüfen: Mengen oder Preise stimmen nicht vollständig überein.",
    );
  }
  if (!order.has_cost_center_context) {
    items.push(
      "Kostenstelle fehlt: kein Projekt, keine Lieferadresse oder Kundenreferenz.",
    );
  }

  return items.length > 0
    ? items
    : [
        "Quellen und Kostenstellenkontext sind für diese Detailansicht vollständig genug.",
      ];
}

function ContextPanel({
  order,
  project,
  fromPath,
}: {
  order: OrderRow;
  project: ProjectSummary | null;
  fromPath: string;
}) {
  const display = projectDisplay(project ?? undefined, order.project_key);
  const deliveryHref = deliveryAddressCostCenterHref(order.delivery_address_key);
  const buyer = order.buyer_name ?? order.buyer_email;
  const varianceHref = priceVarianceHref({
    project: order.project_key ? [order.project_key] : undefined,
    delivery_address_key: order.delivery_address_key
      ? [order.delivery_address_key]
      : undefined,
    supplier: order.supplier ? [order.supplier] : undefined,
    online_user: order.buyer_email ?? undefined,
  });

  return (
    <section className="mt-8">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Kontext
        </h2>
        <Link
          to={varianceHref}
          state={{ from: fromPath }}
          className="text-xs font-medium text-emerald-700 hover:underline"
        >
          Kontext in der Analyse öffnen →
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <ContextItem label="Projekt">
          {order.project_key && display ? (
            <Link
              to={
                projectCostCenterHref(order.project_key) ??
                costCenterListHref("project")
              }
              state={{ from: fromPath }}
              className="text-emerald-700 hover:underline"
              title={display.tooltip}
            >
              {display.meta ? `${display.title} · ${display.meta}` : display.title}
            </Link>
          ) : (
            "–"
          )}
        </ContextItem>
        <ContextItem label="Lieferadresse">
          {deliveryHref ? (
            <Link
              to={deliveryHref}
              state={{ from: fromPath }}
              className="text-emerald-700 hover:underline"
            >
              {order.delivery_address_display ?? order.delivery_address_key}
            </Link>
          ) : (
            (order.delivery_address_display ?? "–")
          )}
        </ContextItem>
        <ContextItem label="Kundenreferenz">
          {order.customer_reference ?? "–"}
        </ContextItem>
        <ContextItem label="Besteller">{buyer ?? "–"}</ContextItem>
      </div>
    </section>
  );
}

function ContextItem({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 min-w-0 truncate text-slate-800" title={stringTitle(children)}>
        {children}
      </div>
    </div>
  );
}

function OrderLinesTable({
  rows,
  emptyMessage,
  fromPath,
  onOpenRawLine,
}: {
  rows: OrderDetailLine[];
  emptyMessage: string;
  fromPath: string;
  onOpenRawLine: (line: OrderDetailLine) => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">
        {emptyMessage}
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[1020px] table-fixed text-sm">
          <colgroup>
            <col className="w-[9%]" />
            <col className="w-[12%]" />
            <col className="w-[11%]" />
            <col className="w-[29%]" />
            <col className="w-[8%]" />
            <col className="w-[9%]" />
            <col className="w-[9%]" />
            <col className="w-[13%]" />
          </colgroup>
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <Th>Datum</Th>
              <Th>Quelle</Th>
              <Th>Beleg</Th>
              <Th>Artikel</Th>
              <Th align="right">Menge</Th>
              <Th align="right">Rabatt</Th>
              <Th align="right">Netto</Th>
              <Th align="right">Ausgaben</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, index) => (
              <tr
                key={`${row.source_kind}-${row.document_id ?? "doc"}-${row.line_pos ?? index}`}
                className={
                  row.source_kind.startsWith("ugl")
                    ? "bg-amber-50/30 hover:bg-amber-50"
                    : "hover:bg-slate-50"
                }
              >
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-slate-700">
                  {formatDate(row.document_date)}
                </td>
                <td className="px-3 py-2">
                  <SourceCell row={row} onOpenRawLine={onOpenRawLine} />
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-600">
                  {row.pdf_doc_id ? (
                    <DocumentPdfActions
                      docId={row.pdf_doc_id}
                      docNumber={row.document_number}
                      sourceKind={row.source_kind}
                      linePos={row.line_pos}
                      pageNo={toNumber(row.page_no)}
                    />
                  ) : (
                    <span>{row.document_number ?? "–"}</span>
                  )}
                  {shouldShowDocumentLine(row) ? (
                    <span className="mt-0.5 block text-[11px] text-slate-400">
                      Zeile {String(row.line_pos)}
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  <ArticleCell row={row} fromPath={fromPath} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">
                  {formatQty(row.qty)}
                  {row.uom ? (
                    <span className="ml-1 text-xs text-slate-400">{row.uom}</span>
                  ) : null}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">
                  {formatPercent(row.discount_pct)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-900">
                  {formatEuro(row.effective_unit_price ?? row.net_unit_price)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium text-slate-900">
                  {formatEuro(row.line_net_amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SourceCell({
  row,
  onOpenRawLine,
}: {
  row: OrderDetailLine;
  onOpenRawLine: (line: OrderDetailLine) => void;
}) {
  return (
    <div className="flex flex-nowrap items-center gap-1.5 whitespace-nowrap">
      <SourceBadge value={row.source_kind} />
      {row.raw_record ? (
        <button
          type="button"
          onClick={() => onOpenRawLine(row)}
          className="rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-100"
        >
          Rohdaten
        </button>
      ) : null}
    </div>
  );
}

function shouldShowDocumentLine(row: OrderDetailLine): boolean {
  return row.line_pos != null && !row.source_kind.startsWith("ugl");
}

function ArticleCell({
  row,
  fromPath,
}: {
  row: OrderDetailLine;
  fromPath: string;
}) {
  const label = row.primary_article_code ?? row.article_group_key;
  const description = row.description
    ? (cleanDescription(row.description) ?? row.description)
    : null;
  // A special article has no article page to open (#556).
  const canOpenArticleDetail =
    row.source_kind === "pdf_rechnung"
    && row.article_group_key !== null
    && !isSpecialArticleKey(row.article_group_key);

  if (!label) {
    return <span className="text-slate-400">–</span>;
  }

  const content = (
    <>
      <span
        className={`block truncate font-mono text-xs ${
          canOpenArticleDetail ? "text-emerald-700 hover:underline" : "text-slate-800"
        }`}
      >
        {label}
      </span>
      {description ? (
        <span className="block truncate text-xs text-slate-500" title={description}>
          {description}
        </span>
      ) : null}
    </>
  );

  if (!canOpenArticleDetail || !row.article_group_key) {
    return <div className="min-w-0">{content}</div>;
  }

  return (
    <Link
      to={`/articles/${encodeURIComponent(row.article_group_key)}`}
      state={{ from: fromPath }}
      className="block min-w-0"
    >
      {content}
    </Link>
  );
}

function rawRecordFromLine(line: OrderDetailLine): RawRecordModalData {
  const sourceLabel = line.source_kind.startsWith("pdf")
    ? "PDF-Rohdaten"
    : "UGL-Rohdaten";
  return {
    title: sourceLabel,
    sourceKind: line.source_kind,
    supplier: null,
    sourceFile: line.source_file,
    sourceRecordRow: line.source_record_row,
    rawRecord: line.raw_record,
  };
}

function StatusBadge({ value }: { value: string | null }) {
  const label = reconciliationLabel(value);
  const tone =
    value === "matched_ugl_order" || value === "matched_pdf_order"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : value === "invoice_without_order" || value === "order_without_invoice"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : "border-slate-200 bg-slate-50 text-slate-600";
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${tone}`}>
      {label}
    </span>
  );
}

function SourceBadge({ value }: { value: string }) {
  return (
    <SourceKindBadge
      kind={sourceBadgeKindFromSourceKind(value)}
      label={sourceKindLabel(value)}
      size="sm"
    />
  );
}

function sourceKindLabel(value: string): string {
  switch (value) {
    case "pdf_rechnung":
      return "PDF Rechnung";
    case "pdf_auftrag":
      return "PDF Auftrag";
    case "ugl_rechnung":
    case "ugl_vorgang":
      return "UGL";
    default:
      return value;
  }
}

function reconciliationLabel(value: string | null): string {
  switch (value) {
    case "matched_ugl_order":
      return "PDF/UGL";
    case "matched_pdf_order":
      return "PDF";
    case "invoice_without_order":
      return "Rechnung offen";
    case "order_without_invoice":
      return "Auftrag offen";
    case "ugl_only":
      return "nur UGL";
    case "pdf_only":
      return "nur PDF";
    default:
      return "offen";
  }
}

function countSources(rows: OrderDetailLine[]): Record<string, number> {
  return {
    pdf_rechnung: rows.filter((row) => row.source_kind === "pdf_rechnung").length,
    pdf_auftrag: rows.filter((row) => row.source_kind === "pdf_auftrag").length,
    ugl_rechnung: rows.filter((row) => row.source_kind === "ugl_rechnung").length,
    ugl_vorgang: rows.filter((row) => row.source_kind === "ugl_vorgang").length,
  };
}

function sourceSummary(counts: Record<string, number>): string {
  const parts = [
    counts.pdf_rechnung > 0 ? "PDF Rechnung" : null,
    counts.pdf_auftrag > 0 ? "PDF Auftrag" : null,
    counts.ugl_rechnung + counts.ugl_vorgang > 0 ? "UGL" : null,
  ].filter(Boolean);
  return parts.join(" + ") || "–";
}

function costCenterHint(order: OrderRow): string {
  return [
    order.project_key ? "Projekt" : null,
    order.delivery_address_key ? "Lieferadresse" : null,
    order.customer_reference ? "Referenz" : null,
  ]
    .filter(Boolean)
    .join(" · ") || "keine Zuordnung";
}

function stringTitle(value: ReactNode): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function Th({
  children,
  align,
}: {
  children: ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`px-3 py-2 font-medium ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}
